import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchOrders, updateOrder } from "../store/ordersSlice.js";
import { ref, update } from "firebase/database";
import { db } from "../firebase.js";

const AdminRatingsManager = () => {
  const dispatch = useDispatch();
  const orders = useSelector((s) => s.orders.items || []);
  const [ratedOrders, setRatedOrders] = useState([]);
  const [selectedRating, setSelectedRating] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedRating, setEditedRating] = useState(0);
  const [editedReview, setEditedReview] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterRating, setFilterRating] = useState("all");

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  useEffect(() => {
    // Filter orders that have ratings
    const rated = orders.filter((order) => order.rating && order.rating > 0);
    setRatedOrders(rated);
  }, [orders]);

  const handleEditRating = (order) => {
    setSelectedRating(order);
    setEditedRating(order.rating);
    setEditedReview(order.review || "");
    setShowEditModal(true);
  };

  const handleSaveRating = async () => {
    if (!selectedRating) return;

    setLoading(true);
    try {
      const updatedOrder = {
        ...selectedRating,
        rating: editedRating,
        review: editedReview,
        ratingEditedAt: Date.now(),
        ratingEditedBy: "admin",
      };

      // Update in Firebase
      if (db) {
        const orderRef = ref(db, `orders/${selectedRating.id}`);
        await update(orderRef, updatedOrder);
      }

      // Update in Redux
      await dispatch(updateOrder({ id: selectedRating.id, data: updatedOrder })).unwrap();

      toast.success("Rating updated successfully!");
      setShowEditModal(false);
      setSelectedRating(null);
    } catch (error) {
      console.error("Error updating rating:", error);
      toast.error("Failed to update rating");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRating = async (order) => {
    if (!window.confirm("Are you sure you want to delete this rating?")) return;

    setLoading(true);
    try {
      const updatedOrder = {
        ...order,
        rating: 0,
        review: "",
        ratingDeletedAt: Date.now(),
        ratingDeletedBy: "admin",
      };

      // Update in Firebase
      if (db) {
        const orderRef = ref(db, `orders/${order.id}`);
        await update(orderRef, updatedOrder);
      }

      // Update in Redux
      await dispatch(updateOrder({ id: order.id, data: updatedOrder })).unwrap();

      toast.success("Rating deleted successfully!");
    } catch (error) {
      console.error("Error deleting rating:", error);
      toast.error("Failed to delete rating");
    } finally {
      setLoading(false);
    }
  };

  const filteredRatings = filterRating === "all" 
    ? ratedOrders 
    : ratedOrders.filter(order => order.rating === parseInt(filterRating));

  const averageRating = ratedOrders.length > 0
    ? (ratedOrders.reduce((sum, order) => sum + order.rating, 0) / ratedOrders.length).toFixed(1)
    : 0;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold">
          <i className="bi bi-star-fill text-warning me-2"></i>
          Ratings & Reviews Management
        </h2>
        <div className="d-flex gap-2">
          <span className="badge bg-primary px-3 py-2">
            Total Ratings: {ratedOrders.length}
          </span>
          <span className="badge bg-warning px-3 py-2">
            Average: {averageRating} ⭐
          </span>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = ratedOrders.filter((o) => o.rating === star).length;
          const percentage = ratedOrders.length > 0 ? ((count / ratedOrders.length) * 100).toFixed(1) : 0;
          return (
            <div key={star} className="col-md-2">
              <div className="card border-0 shadow-sm rounded-4 p-3 text-center">
                <div className="fw-bold mb-2">
                  {star} <i className="bi bi-star-fill text-warning"></i>
                </div>
                <h3 className="fw-bold mb-1">{count}</h3>
                <small className="text-muted">{percentage}%</small>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter */}
      <div className="card border-0 shadow-sm rounded-4 p-3 mb-4">
        <div className="row align-items-center">
          <div className="col-md-3">
            <label className="form-label small fw-semibold">Filter by Rating</label>
            <select
              className="form-select"
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>
          <div className="col-md-9 text-end">
            <small className="text-muted">
              Showing {filteredRatings.length} of {ratedOrders.length} ratings
            </small>
          </div>
        </div>
      </div>

      {/* Ratings Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Product(s)</th>
                  <th>Rating</th>
                  <th>Review</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRatings.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="bi bi-star display-4 d-block mb-3 text-muted"></i>
                      <h5 className="text-muted">No ratings found</h5>
                    </td>
                  </tr>
                ) : (
                  filteredRatings.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <div className="fw-bold text-primary">#{order.id?.slice(0, 8)}</div>
                        <small className="text-muted">{order.id?.slice(8, 16)}</small>
                      </td>
                      <td>
                        <div className="fw-medium">{order.name}</div>
                        <small className="text-muted">{order.email}</small>
                      </td>
                      <td>
                        <div className="small">
                          {order.items?.map((item, idx) => (
                            <div key={idx}>{item.name}</div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i
                              key={star}
                              className={`bi bi-star${star <= order.rating ? "-fill" : ""} text-warning`}
                            ></i>
                          ))}
                          <span className="ms-2 fw-bold">{order.rating}</span>
                        </div>
                      </td>
                      <td>
                        <div className="small" style={{ maxWidth: "300px" }}>
                          {order.review ? (
                            <span className="text-muted fst-italic">"{order.review}"</span>
                          ) : (
                            <span className="text-muted">No review</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          {order.ratedAt
                            ? new Date(order.ratedAt).toLocaleDateString()
                            : "—"}
                        </div>
                        {order.ratingEditedAt && (
                          <small className="text-warning">
                            <i className="bi bi-pencil me-1"></i>Edited
                          </small>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditRating(order)}
                            title="Edit Rating"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteRating(order)}
                            title="Delete Rating"
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedRating && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Rating</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Order ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRating.id?.slice(0, 16)}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Customer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRating.name}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Rating</label>
                  <div className="d-flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`btn ${star <= editedRating ? "btn-warning" : "btn-outline-warning"}`}
                        onClick={() => setEditedRating(star)}
                      >
                        <i className="bi bi-star-fill"></i> {star}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Review</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={editedReview}
                    onChange={(e) => setEditedReview(e.target.value)}
                    placeholder="Customer review..."
                  ></textarea>
                </div>
                <div className="alert alert-warning small">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Editing ratings should be done carefully and only when necessary (e.g., removing inappropriate content).
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveRating}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRatingsManager;
